import React, { useState, useEffect, useRef, useContext } from 'react';
import ListLoadError from '../../components/ListLoadError';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import FinancialsEmptyState from '../../components/Invoicing/FinancialsEmptyState';
import { extractApiError } from '../../utils/errorMessages';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faPlus, faEye, faCheckCircle, faClock, faExclamationTriangle, faArrowRight, faSync, faEdit, faTrash, faClipboardList, faDownload, faFilePdf, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import Footer from '../../components/ui/Footer';
import invoicingService, {
  deliveryScopedListNotice, deliveryScopedEmptyNotice,
  deliveryScopedListHeading, DELIVERY_SCOPED_NOTICE_STYLE,
} from '../../services/invoicingService';
import { AuthContext } from '../../context/AuthContext';
import { hasFinancialAccess, isDeliveryScopedInvoiceReader } from '../../utils/financialAccess';
import { isRestrictedCustomerTrader } from '../../utils/customerTraderAccess';
import InvoiceAuditModal from '../../components/Invoicing/InvoiceAuditModal';
import InvoiceSearchFilter from '../../components/Invoicing/InvoiceSearchFilter';
import ConvertProformaConfirmModal from '../../components/ConvertProformaConfirmModal';
import InvoiceRelationshipModal from '../../components/Invoicing/InvoiceRelationshipModal';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';
import { useFlashMessage } from '../../components/FlashMessage/FlashMessageContext';
import './OutgoingInvoice.css';
import SortableTh from '../../components/shared/SortableTh';
import useTableSort from '../../components/shared/useTableSort';
import useSavedRowFlash from '../../components/shared/useSavedRowFlash';
import SavedRowBanner from '../../components/shared/SavedRowBanner';
import { PROFORMA_INVOICE_COLUMNS } from './listColumns';

/* What this screen actually shows. The delivery-scoped banner used to be
 * hard-coded "Showing your deliveries' invoices." on all four list screens,
 * so an operator on this one was told the wrong noun about the very list they
 * were being asked to trust. */
const SCOPED_DOCUMENT_PLURAL = 'pro-forma invoices';

/**
 * Pro-forma Invoice List Page
 * 
 * Features:
 * - List all pro-forma invoices
 * - Advanced search and filter (using InvoiceSearchFilter component)
 * - Filter by status, entity, partner, date range
 * - Search by invoice number or partner name
 * - Create new pro-forma invoice button
 * - Convert to outgoing invoice action
 * - View invoice details
 * - Approval-based edit/delete restrictions
 */


const ProformaInvoiceList = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  // WHICH READER IS THIS? See IncomingInvoiceList for the full note — the same
  // two readers share all four invoice lists since 2026-09-07. A finance user
  // reads their whole entity book; a trade-ops operator reads only the invoices
  // raised against deliveries they can already open, because
  // invoicing.delivery_scoped_access clamps the rows. This flag keeps the SCREEN
  // honest about that: anything below that the server would refuse is hidden
  // rather than left to fail into an empty panel.
  const { user } = useContext(AuthContext);
  const scopedReader = isDeliveryScopedInvoiceReader(user);
  // A customer trader is an external counterparty carrying has_bunkers_access,
  // so the route lets them in and the server then refuses them. Refuse here too
  // rather than ask on their behalf and draw a bare table.
  const barredTrader = Boolean(user) && isRestrictedCustomerTrader(user) && !hasFinancialAccess(user);
  const [invoices, setInvoices] = useState([]);
  // Column sorting — comparators live in utils/tableSort, column kinds in
  // ./listColumns, so all six invoicing lists sort by the same rules.
  const { sort, onSort, applySort } = useTableSort(PROFORMA_INVOICE_COLUMNS);
  const [loading, setLoading] = useState(true);
  // Refusal or failure text for the banner. Without it a 403 renders as
  // an empty table, which reads as "you have none" rather than "we could
  // not ask".
  const [loadError, setLoadError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);
  
  // Search and filter state
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Audit modal state
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedInvoiceForAudit, setSelectedInvoiceForAudit] = useState(null);
  
  // Relationship modal state
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [selectedInvoiceForRelationship, setSelectedInvoiceForRelationship] = useState(null);
  
  // Convert confirmation modal state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedInvoiceForConversion, setSelectedInvoiceForConversion] = useState(null);

  // PDF preview state - status-agnostic preview to check formatting
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [previewFileName, setPreviewFileName] = useState('');
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  // Component-level flag to prevent double load (resets on unmount)
  const hasLoadedRef = useRef(false);
  
  useEffect(() => {
    // `barredTrader` is false while the profile is still resolving, so re-run on
    // it: the refusal must land the moment we know who is asking.
    if (barredTrader) {
      setInvoices([]);
      loadInvoices();   // takes the refusal branch, makes no request
      return;
    }
    // Load invoices only once on initial mount
    if (!isSearchMode && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchMode, barredTrader]);
  
  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadInvoices = async () => {
    // An external counterparty asking for our pre-invoices is not a load
    // failure to retry, it is an answer.
    if (barredTrader) {
      setLoading(false);
      setLoadError(
        'Pro-forma invoices are internal to IMT. Your account is a customer '
        + 'trader account, which can see its own deliveries but not our invoice '
        + 'book. Contact your IMT representative if you need an invoice.'
      );
      return;
    }
    try {
      setLoading(true);
      setLoadError(null);
      const data = await invoicingService.fetchProformaInvoices({});
      setInvoices(data.results || data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading pro-forma invoices:', error);
      setLoadError(extractApiError(error, 'The pro-forma invoices could not be loaded.'));
      setLoading(false);
    }
  };
  
  const handleSearchResults = (results) => {
    console.log('handleSearchResults called with', results.length, 'results');
    setSearchResults(results);
    
    // Enter search mode if we have results or active filters
    if (results.length > 0 || Object.keys(activeFilters).some(key => activeFilters[key])) {
      console.log('ENTERING SEARCH MODE - we have results or filters');
      setIsSearchMode(true);
    } else {
      console.log('NO RESULTS AND NO FILTERS - staying in search mode but showing empty');
      setIsSearchMode(true);
    }
  };
  
  const handleFiltersChange = (filters) => {
    setActiveFilters(filters);
    // Update search mode based on filters
    const hasFilters = Object.keys(filters).some(key => filters[key]);
    if (hasFilters) {
      setIsSearchMode(true);
    }
  };
  
  const handleSearchStateChange = (searching) => {
    setIsSearching(searching);
  };
  
  const handleClearSearch = () => {
    console.log('handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
    setIsSearchMode(false);
    setSearchResults([]);
    setActiveFilters({});
    loadInvoices();
  };
  
  const handleConvertToOutgoing = (invoice) => {
    if (invoice.converted_to_outgoing) {
      showError('This pro-forma has already been converted to an outgoing invoice.');
      return;
    }
    
    // Open confirmation modal
    setSelectedInvoiceForConversion(invoice);
    setShowConvertModal(true);
  };
  
  const handleConfirmConversion = async () => {
    if (!selectedInvoiceForConversion) return;
    
    try {
      const result = await invoicingService.submitProformaForConversionApproval(selectedInvoiceForConversion.id);
      showSuccess(`Pro forma ${selectedInvoiceForConversion.invoice_number} submitted for conversion approval!`);
      setShowConvertModal(false); // Close modal
      setSelectedInvoiceForConversion(null);
      loadInvoices(); // Reload list
    } catch (error) {
      console.error('Error submitting pro-forma for conversion:', error);
      const errorMsg = error.response?.data?.error || 'Failed to submit pro-forma for conversion. Please try again.';
      showError(errorMsg);
      setShowConvertModal(false); // Close modal even on error
      setSelectedInvoiceForConversion(null);
    }
  };
  
  const getStatusIcon = (status) => {
    // No icons displayed for any status
    return null;
  };
  
  const formatCurrency = (amount, currency) => {
    if (!amount || isNaN(amount)) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  
  const formatReportingMonth = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };
  
  const toggleDropdown = (invoiceId) => {
    setActiveDropdown(activeDropdown === invoiceId ? null : invoiceId);
  };
  
  const handleEdit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    history.push(`/invoicing/proforma/edit/${invoice.id}`);
  };
  
  const handleDelete = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    if (window.confirm(`Are you sure you want to delete pro-forma ${invoice.invoice_number}?`)) {
      try {
        await invoicingService.deleteProformaInvoice(invoice.id);
        loadInvoices();
      } catch (error) {
        console.error('Error deleting pro-forma:', error);
        showError('Failed to delete pro-forma. Please try again.');
      }
    }
  };
  
  const handleDownloadPDF = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    if (invoice.status !== 'APPROVED') {
      showError('Only approved pro-forma invoices can be downloaded.');
      return;
    }
    
    try {
      const blob = await invoicingService.downloadProformaInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ProForma_${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to download PDF. Please try again.');
    }
  };

  const handlePreviewPDF = async (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);

    setIsLoadingPreview(true);
    try {
      const blob = await invoicingService.downloadProformaInvoicePDF(invoice.id, { preview: true });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setPreviewFileName(`ProForma_${invoice.invoice_number}.pdf`);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showError('Failed to load PDF preview. Please try again.');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    setPreviewFileName('');
  };
  
  const handleViewGlobalAudit = () => {
    setSelectedInvoiceForAudit(null);
    setShowAuditModal(true);
  };
  
  const handleViewInvoiceAudit = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForAudit(invoice);
    setShowAuditModal(true);
  };
  
  const handleCloseAuditModal = () => {
    setShowAuditModal(false);
    setSelectedInvoiceForAudit(null);
  };
  
  const handleViewRelationships = (invoice, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    setSelectedInvoiceForRelationship(invoice);
    setShowRelationshipModal(true);
  };
  
  // Sorted wherever the rows came from — the loaded list OR the search
  // results — so the table never behaves differently in the two modes.
  const visibleRows = applySort(isSearchMode ? searchResults : invoices);
  // Point the user at the row they just saved. The creators hand the id
  // over as route state; without it the toast names a row this table
  // gives no way to find (ordered by document date, not by recency).
  const { savedId, rowRef } = useSavedRowFlash({ sort, isSearchMode, activeFilters });
  const savedRowClass = (id) => (savedId !== null && String(id) === String(savedId) ? 'row-just-saved' : '');
  // The banner speaks only about a row that is actually on this page. If the
  // saved row was filtered out or fell beyond the fetched page, saying "see
  // below" would point at nothing -- the toast stands alone instead.
  // A DRAFT has no number yet, so the label falls back to generic wording
  // rather than printing 'undefined' at the user.
  const savedRow = savedId === null
    ? null
    : visibleRows.find(r => String(r.id) === String(savedId)) || null;
  const savedRowLabel = savedRow?.invoice_number || null;

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container proforma-invoice-screen">
        {/* Header */}
        {/* Back: the Invoicing dashboard aggregates the WHOLE book and stays
            finance-only, so sending an operator there walks them straight back
            into the permission bounce this change exists to remove. */}
        <HMSPageHeader
          onBack={() => history.push(scopedReader ? '/dashboard' : '/invoicing')}
          backLabel={scopedReader ? 'Back to Dashboard' : 'Back to Invoicing Dashboard'}
          title="Pro-forma Invoices"
          kicker="Finance"
          subtitle="Pre-invoices for approvals and customs"
          actions={
            <>
              {/* Audit Center reads `audit_logs`, a history of the whole book and
                  not in the delivery-scoped whitelist. For an operator it could
                  only 403, which the modal would draw as "no entries". */}
              {!scopedReader && (
                <button className="btn-secondary" onClick={handleViewGlobalAudit}>
                  <FontAwesomeIcon icon={faClipboardList} /> Audit Center
                </button>
              )}

              {!barredTrader && (
                <button className="btn-primary" onClick={() => history.push('/invoicing/proforma/create')}>
                  <FontAwesomeIcon icon={faPlus} /> Create New Pro-forma
                </button>
              )}
            </>
          }
        />
        
        {/* The search bar drives the SAME `list` action with query params, which
            the scoped tier is allowed and the server clamps identically — so it
            works for an operator and stays mounted. Not rendered at all for a
            barred customer trader, who has no list to search — and not merely
            `hidden`, because `.search-filter-controls` sets `display: flex`, which
            wins over that attribute. */}
        {/* Search and Filter Controls */}
        {!barredTrader && (
        <div className="search-filter-controls">
          <div className="search-filter-wrapper">
            <InvoiceSearchFilter
              onSearchResults={handleSearchResults}
              onFiltersChange={handleFiltersChange}
              isSearching={isSearching}
              onSearchStateChange={handleSearchStateChange}
              onClearSearch={handleClearSearch}
              invoiceType="proforma"
            />
          </div>
          
          <button 
            className="reload-button"
            onClick={loadInvoices}
            title="Reload invoices"
          >
            <FontAwesomeIcon icon={faSync} />
          </button>
        </div>
        )}
        
        {/* Invoice List */}
        <div className="invoice-list-container">
          <ListLoadError message={loadError} onRetry={loadInvoices} />
          {/* Says which entity's book this list is showing. Without it a
              narrowed list reads as "nothing has been raised" — the fault
              reported on 2026-09-03.
              An operator is narrowed by DELIVERY, not by entity (the scoped tier
              skips entity scoping on purpose), so the entity sentence would be a
              plain untruth for them. They get the rule that is actually in
              force. */}
          {scopedReader ? (
            <div role="status" style={DELIVERY_SCOPED_NOTICE_STYLE}>
              <strong style={{ marginRight: '0.4rem' }}>
                {deliveryScopedListHeading(SCOPED_DOCUMENT_PLURAL)}
              </strong>
              {deliveryScopedListNotice(SCOPED_DOCUMENT_PLURAL)}
            </div>
          ) : (
            <EntityScopeNotice emptyResult={!loading && visibleRows.length === 0} />
          )}
          <SavedRowBanner savedId={savedRow ? savedId : null} label={savedRowLabel} />
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading pro-forma invoices...</p>
            </div>
          ) : (
            <table className="invoice-list-table">
              <thead>
                <tr>
                  {PROFORMA_INVOICE_COLUMNS.map((column) => (
                    <SortableTh key={column.key} column={column} sort={sort} onSort={onSort} />
                  ))}
                  {/* Actions holds buttons, not data — nothing to sort by. */}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="empty-state-cell">
                      <div className="empty-state-container">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={faFileContract} />
                        </div>
                        {/* FinancialsEmptyState explains an ENTITY narrowing. An
                            operator is narrowed by delivery instead, so it would name
                            the wrong reason in the largest text on the screen. */}
                        {scopedReader ? (
                          <div className="empty-state-text">
                            <h3>Nothing on your deliveries yet</h3>
                            <p>{deliveryScopedEmptyNotice(SCOPED_DOCUMENT_PLURAL)}</p>
                          </div>
                        ) : (
                          <FinancialsEmptyState
                            label="pro-forma invoices"
                            heading="No Pro-forma Invoices Yet"
                            blurb="Pro-forma invoices will appear here"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRows.map(invoice => (
                    <tr 
                      key={invoice.id} 
                      ref={rowRef(invoice.id)}
                      className={[invoice.converted_to_outgoing ? 'row-converted' : '', savedRowClass(invoice.id)].filter(Boolean).join(' ')}
                      onClick={() => history.push(`/invoicing/proforma/${invoice.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="invoice-number-cell">{invoice.invoice_number || (invoice.status === 'DRAFT' ? 'DRAFT' : 'N/A')}</td>
                      <td>{invoice.hms_entity_name || 'N/A'}</td>
                      <td>{invoice.partner_name || 'N/A'}</td>
                      <td>{invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</td>
                      <td>{formatReportingMonth(invoice.reporting_month)}</td>
                      <td>{invoice.currency || 'USD'}</td>
                      <td className="amount-cell">{formatCurrency(invoice.net_amount, invoice.currency)}</td>
                      <td className="amount-cell">{formatCurrency(invoice.vat_amount, invoice.currency)}</td>
                      <td className="amount-cell">{formatCurrency(invoice.gross_amount, invoice.currency)}</td>
                      <td className="status-cell">
                        {getStatusIcon(invoice.status)}
                        <span className={`status-badge ${invoice.status.toLowerCase().replace(/_/g, '-')}`}>
                          {invoice.status === 'PENDING_CONVERSION_APPROVAL' 
                            ? 'Pending Conversion' 
                            : (invoice.status_display || invoice.status)}
                        </span>
                      </td>
                      <td>
                        {invoice.converted_to_outgoing ? (
                          <span className="converted-badge">
                            CONVERTED
                          </span>
                        ) : (
                          <span className="not-converted-badge">Pending</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <div 
                          className={`action-dropdown ${activeDropdown === invoice.id ? 'dropdown-open' : ''}`}
                          onClick={(e) => e.stopPropagation()}
                          ref={activeDropdown === invoice.id ? dropdownRef : null}
                        >
                          <button 
                            className="gear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(invoice.id);
                            }}
                          >
                            Actions
                          </button>
                          {activeDropdown === invoice.id && (
                            <div className="dropdown-menu">
                              {/* For converted pro formas - show only View Outgoing and Audit */}
                              {invoice.converted_to_outgoing ? (
                                <>
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    // Navigate directly to the converted outgoing invoice
                                    if (invoice.converted_outgoing_invoice_id) {
                                      history.push(`/invoicing/outgoing/${invoice.converted_outgoing_invoice_id}`);
                                    } else {
                                      // Fallback to search if ID not available
                                      history.push(`/invoicing/outgoing?search=${invoice.invoice_number}`);
                                    }
                                  }}>
                                    <FontAwesomeIcon icon={faArrowRight} /> View Outgoing Invoice
                                  </button>
                                  
                                  {/* get_relationships and audit-logs are outside
                                      the delivery-scoped whitelist: both would
                                      403 for an operator and both render a
                                      refusal as an empty panel. */}
                                  {!scopedReader && (
                                    <>
                                      {/* View Relationships */}
                                      <button onClick={(e) => handleViewRelationships(invoice, e)}>
                                        <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                                      </button>

                                      <button onClick={(e) => handleViewInvoiceAudit(invoice, e)}>
                                        <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* View option always available */}
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDropdown(null);
                                    history.push(`/invoicing/proforma/${invoice.id}`);
                                  }}>
                                    <FontAwesomeIcon icon={faEye} /> View
                                  </button>
                                  
                                  {/* get_relationships is outside the
                                      delivery-scoped whitelist. */}
                                  {!scopedReader && (
                                    <button onClick={(e) => handleViewRelationships(invoice, e)}>
                                      <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                                    </button>
                                  )}

                                  {/* Preview PDF - available for any status, no audit log */}
                                  <button onClick={(e) => handlePreviewPDF(invoice, e)} disabled={isLoadingPreview}>
                                    <FontAwesomeIcon icon={faFilePdf} /> {isLoadingPreview ? 'Loading...' : 'Preview PDF'}
                                  </button>

                                  {/* Download PDF - only for APPROVED invoices */}
                                  {invoice.status === 'APPROVED' && (
                                    <button onClick={(e) => handleDownloadPDF(invoice, e)}>
                                      <FontAwesomeIcon icon={faDownload} /> Download PDF
                                    </button>
                                  )}
                                  
                                  {/* Only show Edit if NOT approved */}
                                  {invoice.status !== 'APPROVED' && (
                                    <button onClick={(e) => handleEdit(invoice, e)}>
                                      <FontAwesomeIcon icon={faEdit} /> Edit
                                    </button>
                                  )}
                                  
                                  {/* Audit log, convert-to-outgoing and delete are
                                      all outside the delivery-scoped whitelist:
                                      the audit is the whole book's history, and
                                      converting a pro-forma mints a tax-valid
                                      invoice, which is Finance's act. */}
                                  {!scopedReader && (
                                    <>
                                      {/* Audit logs always available */}
                                      <button onClick={(e) => handleViewInvoiceAudit(invoice, e)}>
                                        <FontAwesomeIcon icon={faClipboardList} /> View Audit Log
                                      </button>

                                      {/* Convert option - only for APPROVED and not already converted */}
                                      {!invoice.converted_to_outgoing && invoice.status === 'APPROVED' && (
                                        <button onClick={(e) => {
                                          e.stopPropagation();
                                          setActiveDropdown(null);
                                          handleConvertToOutgoing(invoice);
                                        }}>
                                          <FontAwesomeIcon icon={faArrowRight} /> Convert to Outgoing
                                        </button>
                                      )}

                                      {/* Only show Delete if NOT approved */}
                                      {invoice.status !== 'APPROVED' && (
                                        <>
                                          <hr />
                                          <button className="danger" onClick={(e) => handleDelete(invoice, e)}>
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                          </button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
        
        {/* Footer */}
        <Footer />
      </div>
      
      {/* Audit Modal */}
      <InvoiceAuditModal
        isOpen={showAuditModal}
        onClose={handleCloseAuditModal}
        invoiceType="proforma"
        invoiceId={selectedInvoiceForAudit?.id || null}
        invoiceNumber={selectedInvoiceForAudit?.invoice_number || null}
      />
      
      {/* Relationship Modal */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => setShowRelationshipModal(false)}
        invoice={selectedInvoiceForRelationship}
        invoiceType="proforma"
      />
      
      {/* Convert to Outgoing Confirmation Modal */}
      <ConvertProformaConfirmModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        onConfirm={handleConfirmConversion}
        proforma={selectedInvoiceForConversion}
      />

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={showPdfPreview}
        pdfUrl={previewPdfUrl}
        fileName={previewFileName}
        onClose={handleClosePdfPreview}
      />
    </Layout>
  );
};

export default ProformaInvoiceList;
